import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronUp, ChevronDown, Trash2, Plus, Upload, Image as ImageIcon, Loader2, Search } from "lucide-react";
import * as L from "lucide-react";
import { cms } from "@/lib/cms";
import { toast } from "sonner";

// ----- Field primitives -----
export const TextField = ({ label, value, onChange, placeholder, testid }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</span>
    <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1.5" data-testid={testid} />
  </label>
);

export const TextareaField = ({ label, value, onChange, rows = 3, testid }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</span>
    <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="mt-1.5" data-testid={testid} />
  </label>
);

export const NumberField = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</span>
    <Input type="number" value={value ?? 0} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} className="mt-1.5" data-testid={testid} />
  </label>
);

export const BoolField = ({ label, value, onChange }) => (
  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
    <Switch checked={!!value} onCheckedChange={onChange} />
    {label}
  </label>
);

export const ImageField = ({ label, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const r = await cms.upload(f); onChange(r.absolute || r.url); toast.success("Uploaded"); }
    catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</span>
      <div className="mt-1.5 flex items-center gap-3">
        {value ? (
          <img src={value} alt="" className="h-12 w-12 rounded-lg border border-slate-200 object-cover" />
        ) : (
          <div className="h-12 w-12 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-400"><ImageIcon className="h-4 w-4" /></div>
        )}
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="https://… or upload" className="flex-1" />
        <label className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-3 h-9 cursor-pointer">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          Upload
          <input type="file" accept="image/*" className="hidden" onChange={onPick} disabled={busy} />
        </label>
      </div>
    </div>
  );
};

// Icon picker — picks a lucide-react icon name (used by Features / Industries)
const ICON_NAMES = [
  "Megaphone", "Bot", "Users", "Users2", "BarChart3", "Workflow", "MessagesSquare", "Shield",
  "Plane", "Building2", "GraduationCap", "Stethoscope", "ShoppingBag", "Car", "Banknote", "Factory",
  "MessageCircle", "Zap", "Sparkles", "Globe", "TrendingUp", "Clock", "Mail", "Phone", "BadgeCheck",
  "ShieldCheck", "Lock", "Bell", "Heart", "Star", "Award", "Trophy", "Send", "Cloud", "Database",
  "Search", "Settings", "Tag", "CheckCircle2", "Plug", "Layers", "Filter", "GitBranch",
  "Brain", "Headphones", "FileText", "Rocket", "Link2", "MessageCircleReply", "MousePointerClick",
  "Activity", "Wifi", "PhoneCall", "ArrowUpRight", "Loader2",
];
export const IconPickerField = ({ label, value, onChange }) => {
  const [q, setQ] = useState("");
  const Icon = L[value] || L.Sparkles;
  const filtered = ICON_NAMES.filter((n) => n.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</span>
      <Popover>
        <PopoverTrigger asChild>
          <button className="mt-1.5 w-full inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50 text-left">
            <Icon className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-medium text-slate-800">{value || "Sparkles"}</span>
            <span className="ml-auto text-xs text-slate-400">change</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2" align="start">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search icons" className="pl-7 h-8 text-sm" />
          </div>
          <div className="mt-2 grid grid-cols-6 gap-1 max-h-64 overflow-y-auto">
            {filtered.map((n) => {
              const I = L[n];
              return (
                <button key={n} onClick={() => onChange(n)} title={n}
                  className={`aspect-square rounded-md flex items-center justify-center hover:bg-emerald-50 ${value === n ? "bg-emerald-100 ring-1 ring-emerald-400" : ""}`}>
                  <I className="h-4 w-4 text-slate-700" />
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// ----- Array editor with up/down/delete/add -----
export const ArrayEditor = ({ items, onChange, renderItem, newItem, label = "Item", testid }) => {
  const list = Array.isArray(items) ? items : [];
  const update = (i, next) => { const c = [...list]; c[i] = next; onChange(c); };
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= list.length) return;
    const c = [...list]; [c[i], c[j]] = [c[j], c[i]]; onChange(c);
  };
  const remove = (i) => { if (!window.confirm("Delete this item?")) return; const c = [...list]; c.splice(i, 1); onChange(c); };
  const add = () => onChange([...list, typeof newItem === "function" ? newItem() : { ...newItem }]);

  return (
    <div className="space-y-3" data-testid={testid}>
      {list.map((it, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-slate-50/40 p-3" data-testid={`${testid}-row-${i}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">{label} #{i + 1}</span>
            <div className="flex items-center gap-0.5">
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(i, -1)} disabled={i === 0} title="Move up">
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(i, 1)} disabled={i === list.length - 1} title="Move down">
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-600 hover:bg-red-50" onClick={() => remove(i)} title="Delete">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {renderItem(it, (next) => update(i, next), i)}
        </div>
      ))}
      <Button type="button" onClick={add} variant="outline" className="rounded-full w-full border-dashed" data-testid={`${testid}-add`}>
        <Plus className="h-4 w-4 mr-1.5" /> Add {label}
      </Button>
    </div>
  );
};
